import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getCxP, getCxPDetail, createMultiLetras, getCuentas, addPagoToCompra } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { FileText, Plus, Trash2, CreditCard, FileCheck, PlusCircle, Zap } from 'lucide-react';

const ESTADO_COLORS = {
  PENDIENTE: 'bg-yellow-100 text-yellow-700',
  PARCIAL: 'bg-orange-100 text-orange-700',
  PAGADO: 'bg-emerald-100 text-emerald-700',
  VENCIDO: 'bg-red-100 text-red-700',
  CANJEADO: 'bg-blue-100 text-blue-700'
};

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'DEPOSITO', label: 'Depósito' },
];

const CxPPage = () => {
  const { empresaActual } = useApp();
  const [cxps, setCxps] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [filtroDocumento, setFiltroDocumento] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  
  // Modal crear letras
  const [crearLetrasOpen, setCrearLetrasOpen] = useState(false);
  const [selectedCxP, setSelectedCxP] = useState(null);
  const [fechaGiro, setFechaGiro] = useState(new Date().toISOString().split('T')[0]);
  const [letrasForm, setLetrasForm] = useState([]);
  const [savingLetras, setSavingLetras] = useState(false);
  const [bancoPagoLetras, setBancoPagoLetras] = useState(''); // Banco donde se pagarán las letras
  
  // Generación rápida de letras
  const [generadorConfig, setGeneradorConfig] = useState({
    cantidad: 3,
    intervaloDias: 30,
    prefijo: 'L001-'
  });
  
  // Modal agregar pago
  const [agregarPagoOpen, setAgregarPagoOpen] = useState(false);
  const [nuevoPago, setNuevoPago] = useState({
    cuenta_id: '',
    medio_pago: 'TRANSFERENCIA',
    monto: '',
    ref_pago: ''
  });
  const [savingPago, setSavingPago] = useState(false);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const [cxpRes, cuentasRes] = await Promise.all([
        getCxP(empresaActual.id, filtroEstado === 'all' ? undefined : filtroEstado),
        getCuentas(empresaActual.id)
      ]);
      setCxps(cxpRes.data);
      setCuentas(cuentasRes.data.filter(c => c.activo));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, filtroEstado]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrar CxPs en el frontend
  const cxpsFiltradas = cxps.filter(cxp => {
    // Filtro por documento
    if (filtroDocumento) {
      const docLower = filtroDocumento.toLowerCase();
      const nroDoc = (cxp.nro_doc_full || '').toLowerCase();
      if (!nroDoc.includes(docLower)) return false;
    }
    // Filtro por proveedor
    if (filtroProveedor) {
      const provLower = filtroProveedor.toLowerCase();
      const provNombre = (cxp.proveedor_nombre || '').toLowerCase();
      if (!provNombre.includes(provLower)) return false;
    }
    return true;
  });

  // Abrir modal de crear letras
  const handleCrearLetras = async (cxp) => {
    try {
      const res = await getCxPDetail(cxp.id);
      setSelectedCxP(res.data);
      setFechaGiro(new Date().toISOString().split('T')[0]);
      setBancoPagoLetras(cuentas[0]?.id?.toString() || ''); // Default al primer banco
      // Iniciar con una letra por el saldo completo
      setLetrasForm([{
        id: 1,
        nro_letra: '',
        fecha_venc: '',
        monto: res.data.saldo_me.toFixed(2)
      }]);
      setCrearLetrasOpen(true);
    } catch (error) {
      toast.error('Error al cargar CxP');
    }
  };

  // Agregar fila de letra
  const addLetraRow = () => {
    const newId = letrasForm.length > 0 ? Math.max(...letrasForm.map(l => l.id)) + 1 : 1;
    setLetrasForm([...letrasForm, { id: newId, nro_letra: '', fecha_venc: '', monto: '' }]);
  };

  // Eliminar fila de letra
  const removeLetraRow = (id) => {
    if (letrasForm.length <= 1) return;
    setLetrasForm(letrasForm.filter(l => l.id !== id));
  };

  // Actualizar fila de letra
  const updateLetraRow = (id, field, value) => {
    setLetrasForm(letrasForm.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  // Generar letras automáticamente
  const generarLetrasAutomaticas = () => {
    if (!selectedCxP) return;
    
    const { cantidad, intervaloDias, prefijo } = generadorConfig;
    const cantNum = parseInt(cantidad) || 1;
    const intervalo = parseInt(intervaloDias) || 30;
    
    if (cantNum < 1 || cantNum > 24) {
      toast.error('La cantidad debe estar entre 1 y 24 letras');
      return;
    }
    
    const saldoDisponible = selectedCxP.saldo_me;
    const montoBase = Math.floor((saldoDisponible / cantNum) * 100) / 100; // Redondear a 2 decimales
    const resto = Math.round((saldoDisponible - (montoBase * cantNum)) * 100) / 100;
    
    const nuevasLetras = [];
    let fechaBase = new Date(fechaGiro);
    
    for (let i = 0; i < cantNum; i++) {
      // Calcular fecha de vencimiento
      const fechaVenc = new Date(fechaBase);
      fechaVenc.setDate(fechaVenc.getDate() + (intervalo * (i + 1)));
      
      // El último recibe el resto para cuadrar
      const monto = i === cantNum - 1 ? (montoBase + resto) : montoBase;
      
      nuevasLetras.push({
        id: i + 1,
        nro_letra: `${prefijo}${String(i + 1).padStart(5, '0')}`,
        fecha_venc: fechaVenc.toISOString().split('T')[0],
        monto: monto.toFixed(2)
      });
    }
    
    setLetrasForm(nuevasLetras);
    toast.success(`${cantNum} letra(s) generada(s) automáticamente`);
  };

  // Guardar letras
  const handleGuardarLetras = async () => {
    // Validaciones
    const letrasValidas = letrasForm.filter(l => l.nro_letra && l.fecha_venc && parseFloat(l.monto) > 0);
    if (letrasValidas.length === 0) {
      toast.error('Complete al menos una letra con número, fecha y monto');
      return;
    }

    if (!bancoPagoLetras) {
      toast.error('Seleccione el banco donde se pagarán las letras');
      return;
    }

    const totalLetras = letrasValidas.reduce((sum, l) => sum + parseFloat(l.monto), 0);
    if (totalLetras > selectedCxP.saldo_me + 0.01) {
      toast.error(`El total de letras (${totalLetras.toFixed(2)}) excede el saldo (${selectedCxP.saldo_me.toFixed(2)})`);
      return;
    }

    setSavingLetras(true);
    try {
      await createMultiLetras({
        cxp_id: selectedCxP.id,
        fecha_giro: fechaGiro,
        cuenta_pago_id: parseInt(bancoPagoLetras),
        letras: letrasValidas.map(l => ({
          nro_letra: l.nro_letra,
          fecha_venc: l.fecha_venc,
          monto: parseFloat(l.monto)
        }))
      });
      toast.success(`${letrasValidas.length} letra(s) creada(s)`);
      setCrearLetrasOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear letras');
    } finally {
      setSavingLetras(false);
    }
  };

  // Abrir modal agregar pago
  const handleAgregarPago = (cxp) => {
    setSelectedCxP(cxp);
    setNuevoPago({
      cuenta_id: cuentas[0]?.id?.toString() || '',
      medio_pago: 'TRANSFERENCIA',
      monto: cxp.saldo_me.toFixed(2),
      ref_pago: `Pago de ${cxp.nro_doc_full || `CxP #${cxp.id}`}`
    });
    setAgregarPagoOpen(true);
  };

  // Guardar pago
  const handleGuardarPago = async () => {
    if (!nuevoPago.cuenta_id || !nuevoPago.monto || parseFloat(nuevoPago.monto) <= 0) {
      toast.error('Complete los datos del pago');
      return;
    }

    if (!selectedCxP.compra_id) {
      toast.error('Esta CxP no tiene factura asociada para registrar pago');
      return;
    }

    setSavingPago(true);
    try {
      await addPagoToCompra(selectedCxP.compra_id, {
        cuenta_id: parseInt(nuevoPago.cuenta_id),
        medio_pago: nuevoPago.medio_pago,
        monto: parseFloat(nuevoPago.monto),
        ref_pago: nuevoPago.ref_pago
      });
      toast.success('Pago registrado');
      setAgregarPagoOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSavingPago(false);
    }
  };

  // Calcular total de letras en el formulario
  const totalLetrasForm = letrasForm.reduce((sum, l) => sum + (parseFloat(l.monto) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-orange-600" />
            Cuentas por Pagar
          </h1>
          <p className="text-gray-500">Gestiona tus obligaciones pendientes</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm text-gray-600">N° Documento</Label>
            <Input
              placeholder="Buscar por documento..."
              value={filtroDocumento}
              onChange={(e) => setFiltroDocumento(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Proveedor</Label>
            <Input
              placeholder="Buscar por proveedor..."
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Estado</Label>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                <SelectItem value="PARCIAL">Parcial</SelectItem>
                <SelectItem value="CANJEADO">Canjeado</SelectItem>
                <SelectItem value="PAGADO">Pagado</SelectItem>
                <SelectItem value="VENCIDO">Vencido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setFiltroDocumento('');
                setFiltroProveedor('');
                setFiltroEstado('all');
              }}
              className="w-full"
            >
              Limpiar filtros
            </Button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Documento</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Letras</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : cxpsFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    {cxps.length === 0 ? 'No hay cuentas por pagar' : 'No se encontraron resultados con los filtros aplicados'}
                  </TableCell>
                </TableRow>
              ) : cxpsFiltradas.map(cxp => (
                <TableRow key={cxp.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">
                    {cxp.nro_doc_full || `CxP #${cxp.id}`}
                  </TableCell>
                  <TableCell>{cxp.proveedor_nombre || '-'}</TableCell>
                  <TableCell>{cxp.fecha}</TableCell>
                  <TableCell>{cxp.fecha_venc || '-'}</TableCell>
                  <TableCell className="text-right font-medium">
                    {cxp.moneda_codigo} {cxp.monto_total_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${cxp.saldo_me > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                    {cxp.moneda_codigo} {cxp.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={ESTADO_COLORS[cxp.estado]}>{cxp.estado}</Badge>
                      {cxp.estado === 'CANJEADO' && cxp.saldo_me > 0.01 && (
                        <span className="text-xs text-orange-600 font-medium">Falta generar letras</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {cxp.num_letras > 0 ? (
                      <Badge variant="outline" className="text-blue-600 border-blue-300">
                        {cxp.num_letras} letra(s)
                      </Badge>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* Botón Pagar - solo si hay saldo y no está canjeado */}
                      {cxp.saldo_me > 0.01 && cxp.estado !== 'CANJEADO' && cxp.compra_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAgregarPago(cxp)}
                          title="Registrar Pago"
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <CreditCard className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Botón Ver/Crear Letras - siempre visible si tiene letras o saldo */}
                      {(cxp.num_letras > 0 || (cxp.saldo_me > 0.01 && cxp.estado !== 'PAGADO')) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCrearLetras(cxp)}
                          title={
                            cxp.estado === 'CANJEADO' && cxp.saldo_me > 0.01 
                              ? "Generar más letras (saldo pendiente)" 
                              : cxp.num_letras > 0 
                                ? "Ver Letras" 
                                : "Canjear por Letras"
                          }
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <FileCheck className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal Crear Letras */}
      <Dialog open={crearLetrasOpen} onOpenChange={setCrearLetrasOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              {selectedCxP?.letras?.length > 0 ? 'Letras de la CxP' : 'Canjear por Letras'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCxP && (
            <div className="space-y-4">
              {/* Info del documento */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Documento</p>
                    <p className="font-medium">{selectedCxP.nro_doc_full || `CxP #${selectedCxP.id}`}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Proveedor</p>
                    <p className="font-medium">{selectedCxP.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Monto Total CxP</p>
                    <p className="font-bold text-lg">
                      {selectedCxP.moneda_codigo} {selectedCxP.monto_total_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Letras existentes */}
              {selectedCxP.letras && selectedCxP.letras.length > 0 && (
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <FileCheck className="w-4 h-4" />
                    Letras Vinculadas ({selectedCxP.letras.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedCxP.letras.map(letra => (
                      <div key={letra.id} className="flex justify-between items-center bg-white p-3 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-4">
                          <span className="font-mono font-medium text-blue-700">{letra.nro_letra}</span>
                          <span className="text-sm text-gray-500">Vence: {letra.fecha_venc}</span>
                          {letra.cuenta_nombre && (
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                              Banco: {letra.cuenta_nombre}
                            </span>
                          )}
                          <Badge className={letra.estado === 'ACTIVA' ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700'}>
                            {letra.estado}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{selectedCxP.moneda_codigo} {letra.monto_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                          {letra.saldo_me < letra.monto_me && (
                            <p className="text-xs text-gray-500">Saldo: {letra.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200 flex justify-between">
                    <span className="font-medium text-blue-800">Total en Letras</span>
                    <span className="font-bold text-blue-800">
                      {selectedCxP.moneda_codigo} {selectedCxP.letras.reduce((sum, l) => sum + l.monto_me, 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

              {/* Sección para crear nuevas letras - solo si hay saldo */}
              {selectedCxP.saldo_me > 0.01 && (
                <>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-800">Agregar Nuevas Letras</h4>
                        <p className="text-sm text-gray-500">Saldo disponible: <span className="font-bold text-orange-600">{selectedCxP.moneda_codigo} {selectedCxP.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span></p>
                      </div>
                    </div>
                  </div>

                  {/* Generación Rápida */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-purple-800">Generación Rápida</h4>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-gray-600">Prefijo</Label>
                        <Input
                          value={generadorConfig.prefijo}
                          onChange={(e) => setGeneradorConfig({...generadorConfig, prefijo: e.target.value})}
                          placeholder="L001-"
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          max="24"
                          value={generadorConfig.cantidad}
                          onChange={(e) => setGeneradorConfig({...generadorConfig, cantidad: e.target.value})}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Intervalo (días)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={generadorConfig.intervaloDias}
                          onChange={(e) => setGeneradorConfig({...generadorConfig, intervaloDias: e.target.value})}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          onClick={generarLetrasAutomaticas}
                          className="h-9 w-full bg-purple-600 hover:bg-purple-700"
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Generar
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Divide S/. {selectedCxP.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })} en {generadorConfig.cantidad} letras = S/. {(selectedCxP.saldo_me / (parseInt(generadorConfig.cantidad) || 1)).toLocaleString('es-PE', { minimumFractionDigits: 2 })} c/u
                    </p>
                  </div>

                  {/* Fecha de giro y Banco de pago */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fecha de Giro</Label>
                      <Input
                        type="date"
                        value={fechaGiro}
                        onChange={(e) => setFechaGiro(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Banco donde se pagarán las letras *</Label>
                      <Select value={bancoPagoLetras} onValueChange={setBancoPagoLetras}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar banco" />
                        </SelectTrigger>
                        <SelectContent>
                          {cuentas.map(c => (
                            <SelectItem key={c.id} value={c.id.toString()}>
                              {c.nombre} ({c.moneda_codigo})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Tabla de letras a crear */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Letras a Crear</Label>
                      <Button variant="outline" size="sm" onClick={addLetraRow}>
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar Letra
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-32">N° Letra *</TableHead>
                            <TableHead className="w-40">Fecha Venc. *</TableHead>
                            <TableHead className="w-32">Monto *</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {letrasForm.map((letra, idx) => (
                            <TableRow key={letra.id}>
                              <TableCell>
                                <Input
                                  value={letra.nro_letra}
                                  onChange={(e) => updateLetraRow(letra.id, 'nro_letra', e.target.value)}
                                  placeholder="L001-00001"
                                  className="h-8"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="date"
                                  value={letra.fecha_venc}
                                  onChange={(e) => updateLetraRow(letra.id, 'fecha_venc', e.target.value)}
                                  className="h-8"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={letra.monto}
                                  onChange={(e) => updateLetraRow(letra.id, 'monto', e.target.value)}
                                  placeholder="0.00"
                                  className="h-8 text-right"
                                />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLetraRow(letra.id)}
                              disabled={letrasForm.length <= 1}
                              className="h-8 w-8 p-0 text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Resumen */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Total Nuevas Letras</span>
                    <span className={`font-bold text-lg ${totalLetrasForm > selectedCxP.saldo_me ? 'text-red-600' : 'text-blue-600'}`}>
                      {selectedCxP.moneda_codigo} {totalLetrasForm.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {totalLetrasForm < selectedCxP.saldo_me - 0.01 && (
                    <p className="text-sm text-gray-500 mt-1">
                      Saldo restante: {selectedCxP.moneda_codigo} {(selectedCxP.saldo_me - totalLetrasForm).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  {totalLetrasForm > selectedCxP.saldo_me + 0.01 && (
                    <p className="text-sm text-red-600 mt-1">
                      ⚠ El total excede el saldo disponible
                    </p>
                  )}
                </div>
              </div>
                </>
              )}

              {/* Mensaje si no hay saldo y no hay letras */}
              {selectedCxP.saldo_me <= 0.01 && (!selectedCxP.letras || selectedCxP.letras.length === 0) && (
                <div className="text-center py-4 text-gray-500">
                  No hay saldo disponible para crear letras
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCrearLetrasOpen(false)}>
              {selectedCxP?.saldo_me <= 0.01 ? 'Cerrar' : 'Cancelar'}
            </Button>
            {selectedCxP?.saldo_me > 0.01 && (
              <Button 
                onClick={handleGuardarLetras} 
                disabled={savingLetras || totalLetrasForm > (selectedCxP?.saldo_me || 0) + 0.01}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingLetras ? 'Guardando...' : 'Crear Letras'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Agregar Pago */}
      <Dialog open={agregarPagoOpen} onOpenChange={setAgregarPagoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-600" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          
          {selectedCxP && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{selectedCxP.nro_doc_full || `CxP #${selectedCxP.id}`}</p>
                    <p className="text-sm text-gray-500">{selectedCxP.proveedor_nombre}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Saldo Pendiente</p>
                    <p className="font-bold text-lg text-orange-600">
                      {selectedCxP.moneda_codigo} {selectedCxP.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Cuenta *</Label>
                  <Select value={nuevoPago.cuenta_id} onValueChange={(v) => setNuevoPago({...nuevoPago, cuenta_id: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccione cuenta" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuentas.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>
                          {c.nombre} ({c.moneda_codigo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Medio de Pago *</Label>
                  <Select value={nuevoPago.medio_pago} onValueChange={(v) => setNuevoPago({...nuevoPago, medio_pago: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEDIOS_PAGO.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    className="mt-1"
                    value={nuevoPago.monto}
                    onChange={(e) => setNuevoPago({...nuevoPago, monto: e.target.value})}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label>Referencia</Label>
                  <Input
                    className="mt-1"
                    value={nuevoPago.ref_pago}
                    onChange={(e) => setNuevoPago({...nuevoPago, ref_pago: e.target.value})}
                    placeholder="Nro. de operación, cheque, etc."
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAgregarPagoOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardarPago} 
              disabled={savingPago}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPago ? 'Guardando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CxPPage;
